/** State adapter for the shared league preset controls. */

import { memo, useCallback, useMemo } from "react";

import LeaguePresetControlsView from "./LeaguePresetControlsView";

import {
  getLeaguePresetIds,
  toggleLeaguePreset,
  type LeaguePresetOption,
} from "@/features/player-filter/utils/leaguePresets";

/** League preset control group props. */
interface Props {
  readonly leagueOptions: readonly LeaguePresetOption[];
  readonly selectedLeagueIds: readonly string[];
  readonly onChange: (ids: readonly string[]) => void;
}

/**
 * Resolves org-available preset IDs and immutable toggle handlers.
 * @param root0 - Control group props
 * @param root0.leagueOptions - Organization-scoped league options
 * @param root0.selectedLeagueIds - Current league selection
 * @param root0.onChange - Immutable selection callback
 */
const LeaguePresetControlsContainer = ({
  leagueOptions,
  selectedLeagueIds,
  onChange,
}: Props) => {
  const big5Ids = useMemo(
    () => getLeaguePresetIds("big5", leagueOptions),
    [leagueOptions]
  );
  const latamIds = useMemo(
    () => getLeaguePresetIds("latam", leagueOptions),
    [leagueOptions]
  );
  const onToggleBig5 = useCallback(
    () => onChange(toggleLeaguePreset(selectedLeagueIds, big5Ids)),
    [big5Ids, onChange, selectedLeagueIds]
  );
  const onToggleLatam = useCallback(
    () => onChange(toggleLeaguePreset(selectedLeagueIds, latamIds)),
    [latamIds, onChange, selectedLeagueIds]
  );

  return (
    <LeaguePresetControlsView
      big5Selected={
        big5Ids.length > 0 &&
        big5Ids.every(id => selectedLeagueIds.includes(id))
      }
      big5Disabled={big5Ids.length === 0}
      latamSelected={
        latamIds.length > 0 &&
        latamIds.every(id => selectedLeagueIds.includes(id))
      }
      latamDisabled={latamIds.length === 0}
      onToggleBig5={onToggleBig5}
      onToggleLatam={onToggleLatam}
    />
  );
};

export default memo(LeaguePresetControlsContainer);
